import React, { useEffect, useState } from "react";
import { useForm, useSelect } from "@refinedev/antd";
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Row,
  Col,
  Typography,
  Tooltip,
  Badge,
  message,
  Space,
  Switch,
  DatePicker,
  Alert
} from "antd";
import type { FormListFieldData, FormListOperation } from "antd/es/form/FormList";
import { MinusCircleOutlined, PlusOutlined, InfoCircleOutlined, ShoppingCartOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../api";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "../../components/ui";

const { Text } = Typography;

interface SaleItem {
  saleId: number;
  InventoryItemId?: number;
  itemCode: string;
  itemName: string;
  itemClassCode: string;
  quantity: number;
  taxRate: number;
  unitPrice: number;
  discountRate: number;
  discountAmount: number;
  totalAmount: number;
  taxAmount: number;
  taxableAmount: number;
  isActive: boolean;
  priceId?: number;
  taxType?: string;
}

interface Payment {
  PaymentMethod: string;
  Amount: number;
  currency: string;
  exchangeRate?: number;
  // UI-only: cashier-friendly display rate (units of currency per 1 USD).
  exchangeRateDisplay?: number;
}

const PAYMENT_METHOD_OPTIONS = [
  { label: 'Cash', value: 'CASH' },
  { label: 'Card', value: 'CARD' },
  { label: 'Mobile Money', value: 'MOBILE_MONEY' },
  { label: 'Bank Transfer', value: 'BANK_TRANSFER' },
  { label: 'Cheque', value: 'CHEQUE' },
];

interface Tax {
  TaxTypeCode: string;
  TaxableAmount: number;
  TaxAmount: number;
  TaxPercent: number;
}

interface TaxOption {
  label: string;
  value: number;
  data: {
    id: number;
    taxType: string;
    rate: number;
    isActive: boolean;
  };
}

interface BillableService {
  description: string;
  price: number;
  taxId?: number;
  taxRate?: number;
  taxType?: string;
}

interface SaleFormData {
  invoiceNo: string;
  customerId?: number;
  customerTPIN: string;
  customerName: string;
  customerContact?: string;
  customerStreet?: string;
  customerCity?: string;
  customerProvince?: string;
  customerCountry?: string;
  salesStatusCode: string;
  currencyTypeCode: string;
  exchangeRate: number;
  // UI-only field: units of transaction currency per 1 USD.
  exchangeRateDisplay?: number;
  exchangeRateDate: string;
  remark: string;
  totalItemCount: number;
  totalTaxableAmount: number;
  totalTax: number;
  totalAmount: number;
  isActive: boolean;
  status: string;
  transactionDate: string;
  itemList: SaleItem[];
  payments: Payment[];
  taxes: Tax[];
  billableServices?: BillableService[];
}

type TenderPanelProps = {
  fields: FormListFieldData[];
  onAdd: FormListOperation['add'];
  onRemove: FormListOperation['remove'];
  form: any;
  currencyOptions: any[];
  getConfiguredCurrencyRate: (code: string) => number;
  convertCurrencyAmount: (
    amount: number,
    fromCurrencyCode: string,
    toCurrencyCode: string,
    transactionCurrencyCode: string,
    transactionRate: number
  ) => number;
};

// Split-currency tender panel. Each row captures Method / Currency / Amount / Rate and
// computes its sale-currency equivalent live so the cashier can see balance + change.
const TenderPanel: React.FC<TenderPanelProps> = ({
  fields,
  onAdd,
  onRemove,
  form,
  currencyOptions,
  getConfiguredCurrencyRate,
  convertCurrencyAmount,
}) => {
  const currencyOptionList = (currencyOptions || []).map((c: any) => ({
    label: c.currencyCode,
    value: c.currencyCode,
  }));

  const handleCurrencyChange = (code: string, index: number) => {
    const currentRow = form.getFieldValue(['payments', index]) || {};
    const usdPerUnit = getConfiguredCurrencyRate(code);
    form.setFieldValue(['payments', index], {
      ...currentRow,
      currency: code,
      exchangeRate: usdPerUnit,
      exchangeRateDisplay: code === "USD" ? 1 : (usdPerUnit > 0 ? 1 / usdPerUnit : 1),
    });
  };

  const handleTenderRateDisplayChange = (unitsPerUsd: number | null, index: number) => {
    const currentRow = form.getFieldValue(['payments', index]) || {};
    const v = Number(unitsPerUsd) || 0;
    const usdPerUnit = v > 0 ? 1 / v : undefined;
    form.setFieldValue(['payments', index], {
      ...currentRow,
      exchangeRate: usdPerUnit,
      exchangeRateDisplay: unitsPerUsd ?? undefined,
    });
  };

  return (
    <Form.Item
      shouldUpdate={(prev: any, curr: any) =>
        prev.payments !== curr.payments ||
        prev.totalAmount !== curr.totalAmount ||
        prev.currencyTypeCode !== curr.currencyTypeCode ||
        prev.exchangeRate !== curr.exchangeRate
      }
      noStyle
    >
      {({ getFieldValue }: any) => {
        const saleCurrency = getFieldValue('currencyTypeCode') || 'USD';
        const saleRate = Number(getFieldValue('exchangeRate')) || 1;
        const total = Number(getFieldValue('totalAmount')) || 0;
        const payments: Payment[] = getFieldValue('payments') || [];

        const tendered = payments.reduce((sum, p) => {
          const amount = Number(p?.Amount) || 0;
          const tenderRate = Number(p?.exchangeRate) || getConfiguredCurrencyRate(p?.currency || saleCurrency);
          return (
            sum +
            convertCurrencyAmount(amount, p?.currency || saleCurrency, saleCurrency, saleCurrency, saleRate || tenderRate)
          );
        }, 0);
        const balance = total - tendered;

        return (
          <>
            {fields.map(({ key, name, ...restField }) => {
              const row = payments[name] || { PaymentMethod: 'CASH', Amount: 0, currency: saleCurrency };
              const rowCurrency = row.currency || saleCurrency;
              const rowRate = Number(row.exchangeRate) || getConfiguredCurrencyRate(rowCurrency);
              const converted = convertCurrencyAmount(
                Number(row.Amount) || 0,
                rowCurrency,
                saleCurrency,
                saleCurrency,
                saleRate || rowRate
              );

              return (
                <Row gutter={[8, 8]} key={key} align="middle" style={{ marginBottom: 8 }}>
                  <Col xs={24} sm={6}>
                    <Form.Item
                      {...restField}
                      name={[name, 'PaymentMethod']}
                      rules={[{ required: true, message: 'Method' }]}
                      style={{ marginBottom: 0 }}
                    >
                      <Select options={PAYMENT_METHOD_OPTIONS} placeholder="Method" />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={4}>
                    <Form.Item
                      {...restField}
                      name={[name, 'currency']}
                      rules={[{ required: true, message: 'Currency' }]}
                      style={{ marginBottom: 0 }}
                    >
                      <Select
                        options={currencyOptionList}
                        placeholder="Currency"
                        onChange={(code: string) => handleCurrencyChange(code, name)}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={5}>
                    <Form.Item
                      {...restField}
                      name={[name, 'Amount']}
                      rules={[{ required: true, message: 'Amount' }]}
                      style={{ marginBottom: 0 }}
                    >
                      <InputNumber min={0} max={999999999} precision={2} style={{ width: '100%' }} placeholder="Amount" />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={4}>
                    <Form.Item
                      {...restField}
                      name={[name, 'exchangeRateDisplay']}
                      style={{ marginBottom: 0 }}
                    >
                      <InputNumber
                        min={0.0001}
                        max={999999}
                        precision={4}
                        style={{ width: '100%' }}
                        placeholder="1 USD = ?"
                        onChange={(val: number | null) => handleTenderRateDisplayChange(val, name)}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={10} sm={4}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      = {saleCurrency} {converted.toFixed(2)}
                    </Text>
                  </Col>
                  <Col xs={2} sm={1}>
                    <Button
                      type="text"
                      danger
                      icon={<MinusCircleOutlined />}
                      disabled={fields.length <= 1}
                      onClick={() => onRemove(name)}
                    />
                  </Col>
                </Row>
              );
            })}

            <Button
              type="dashed"
              block
              icon={<PlusOutlined />}
              onClick={() =>
                onAdd({
                  PaymentMethod: 'CASH',
                  Amount: Math.max(total - tendered, 0),
                  currency: saleCurrency,
                  exchangeRate: saleRate,
                })
              }
              style={{ marginTop: 4 }}
            >
              Add Tender
            </Button>

            <div style={{ marginTop: 12, borderTop: '1px solid var(--surface-border)', paddingTop: 12 }}>
              <Row justify="space-between">
                <Text type="secondary">Total Due:</Text>
                <Text strong>{saleCurrency} {total.toFixed(2)}</Text>
              </Row>
              <Row justify="space-between">
                <Text type="secondary">Tendered:</Text>
                <Text>{saleCurrency} {tendered.toFixed(2)}</Text>
              </Row>
              <Row justify="space-between">
                <Text type="secondary">{balance >= 0 ? 'Balance:' : 'Change:'}</Text>
                <Text strong style={{ color: balance > 0.009 ? '#d97706' : balance < -0.009 ? '#2563eb' : '#10b981' }}>
                  {saleCurrency} {Math.abs(balance).toFixed(2)}
                </Text>
              </Row>
            </div>
          </>
        );
      }}
    </Form.Item>
  );
};

export const SaleCreate: React.FC = () => {
  const navigate = useNavigate();
  const [isCashSale, setIsCashSale] = useState(false);
  const [showBillableServices, setShowBillableServices] = useState(false);
  const [isCustomerCollapsed, setIsCustomerCollapsed] = useState(false);
  const [isManualCustomerEntry, setIsManualCustomerEntry] = useState(false);
  const [standardTaxRate, setStandardTaxRate] = useState(15.5);
  const [taxOptions, setTaxOptions] = useState<TaxOption[]>([]);
  const [selectedCurrencyRate, setSelectedCurrencyRate] = useState<number>(1);
  const [isBaseCurrency, setIsBaseCurrency] = useState(true);

  const { form, formProps, saveButtonProps } = useForm({
    resource: "sales",
    redirect: "list",
    meta: {
      fields: [
        "invoiceNo",
        "customerTPIN",
        "customerName",
        "salesStatusCode",
        "currencyTypeCode",
        "remark",
        "totalItemCount",
        "totalTaxableAmount",
        "totalTax",
        "totalAmount",
        "isActive",
        "status",
        "transactionDate",
        {
          itemList: [
            "saleId",
            "InventoryItemId",
            "itemCode",
            "itemName",
            "itemClassCode",
            "quantity",
            "taxRate",
            "unitPrice",
            "discountRate",
            "discountAmount",
            "totalAmount",
            "taxAmount",
            "taxableAmount",
            "isActive",
            "taxType"
          ]
        },
        {
          payments: [
            "PaymentMethod",
            "Amount",
            "currency"
          ]
        },
        {
          taxes: [
            "TaxTypeCode",
            "TaxableAmount",
            "TaxAmount",
            "TaxPercent"
          ]
        },
        {
          billableServices: [
            "description",
            "price",
            "taxId",
            "taxRate",
            "taxType"
          ]
        }
      ]
    }
  });

  const { selectProps: customerSelectProps, queryResult: customerQueryResult } = useSelect({
    resource: "customers",
    optionLabel: "name",
    optionValue: "tpin",
    meta: {
      fields: [
        "id",
        "name",
        "tpin",
        "code",
        "customerNumber",
        "description",
        "isActive",
        {
          customerContacts: [
            "id",
            "type",
            "value",
            "isPrimary"
          ]
        },
        {
          customerAddresses: [
            "id",
            "type",
            "street",
            "city",
            "state",
            "country",
            "postalCode",
            "isPrimary"
          ]
        }
      ]
    },
    onSearch: (value: string) => [
      {
        field: "name",
        operator: "contains",
        value,
      },
    ],
  });

  const { selectProps: itemSelectProps, queryResult: itemQueryResult } = useSelect({
    resource: "inventory/available",
    optionLabel: "itemName",
    optionValue: "itemCode",
    meta: {
      fields: [
        "id",
        "itemCode",
        "itemTypeCode",
        "itemClassificationCode",
        "itemName",
        "stockLevelId",
        "taxId",
        "barcode",
        "additionalInformation",
        "isActive",
        {
          stockLevel: [
            "id",
            "minimumQuantity",
            "maximumQuantity",
            "reorderPoint",
            "lastUpdated",
            "itemCode",
            "currentStockLevel",
            "reservedStock",
            "availableStock"
          ]
        },
        {
          tax: [
            "id",
            "taxType",
            "taxTypeId",
            "rate",
            "isActive"
          ]
        },
        {
          prices: [
            "id",
            "itemCode",
            "inventoryItemId",
            "basePrice",
            "discountType",
            "discountValue",
            "minimumQuantity",
            "maximumQuantity",
            "effectivePrice",
            "isActive",
            "currencyCode",
            "startDate",
            "endDate"
          ]
        }
      ]
    },
    onSearch: (value: string) => [
      {
        field: "itemCode",
        operator: "contains",
        value,
      },
      {
        field: "itemName",
        operator: "contains",
        value,
      }
    ]
  });

  const { selectProps: currencySelectProps, queryResult: currencyQueryResult } = useSelect({
    resource: "currency/active",
    optionLabel: "currencyCode",
    optionValue: "currencyCode",
    meta: {
      fields: ["id", "currencyCode", "currencyName", "exchangeRate", "isBaseCurrency", "isActive"]
    }
  });

  const currencyOptionsList: any[] =
    currencyQueryResult?.data?.data ??
    currencyQueryResult?.data ??
    [];

  /** Same rule as backend CurrencyExchangeRate: >= 5 means legacy "local currency per 1 USD". */
  const LEGACY_UNITS_PER_USD_THRESHOLD = 5;

  const normalizeToUsdPerUnit = (rawRate: number, isBaseCurrency: boolean): number => {
    const r = Number(rawRate) || 0;
    if (isBaseCurrency || r <= 0) return 1;
    if (r >= LEGACY_UNITS_PER_USD_THRESHOLD) return 1 / r;
    return r;
  };

  const unitsOfCurrencyPerUsd = (usdPerUnit: number): number => {
    const v = Number(usdPerUnit) || 0;
    if (v <= 0) return 0;
    return 1 / v;
  };

  const getConfiguredCurrencyRate = (currencyCode: string): number => {
    if (!currencyCode || currencyCode === "USD") return 1;
    const currencyData = currencyOptionsList?.find((c: any) => c.currencyCode === currencyCode);
    if (!currencyData) return 1;
    if (currencyData.isBaseCurrency) return 1;
    const raw = Number(currencyData.exchangeRate) || 1;
    const normalized = normalizeToUsdPerUnit(raw, !!currencyData.isBaseCurrency);
    return normalized > 0 ? normalized : 1;
  };

  const toDisplayRate = (usdPerUnit: number): number => {
    const v = Number(usdPerUnit) || 0;
    if (v <= 0) return 0;
    return unitsOfCurrencyPerUsd(v);
  };

  const toUsdPerUnitRate = (unitsPerUsd: number): number => {
    const v = Number(unitsPerUsd) || 0;
    if (v <= 0) return 0;
    return 1 / v;
  };

  const convertCurrencyAmount = (
    amount: number,
    fromCurrencyCode: string,
    toCurrencyCode: string,
    transactionCurrencyCode: string,
    transactionRate: number
  ): number => {
    if (!fromCurrencyCode || !toCurrencyCode || fromCurrencyCode === toCurrencyCode) return amount;

    const fromRate = fromCurrencyCode === "USD"
      ? 1
      : (fromCurrencyCode === transactionCurrencyCode ? transactionRate : getConfiguredCurrencyRate(fromCurrencyCode));
    const toRate = toCurrencyCode === "USD"
      ? 1
      : (toCurrencyCode === transactionCurrencyCode ? transactionRate : getConfiguredCurrencyRate(toCurrencyCode));

    if (fromRate <= 0 || toRate <= 0) return amount;

    const usdAmount = fromCurrencyCode === "USD" ? amount : (amount * fromRate);
    const targetAmount = toCurrencyCode === "USD" ? usdAmount : (usdAmount / toRate);
    return targetAmount;
  };

  const recalculateItemPricesForTransactionCurrency = (
    transactionCurrencyCode: string,
    transactionRate: number
  ) => {
    const currentItems: SaleItem[] = form.getFieldValue('itemList') || [];
    if (currentItems.length === 0) return;

    const updatedItems = currentItems.map((item: SaleItem) => {
      const priceId = item.priceId;
      const itemCode = item.itemCode;
      if (!priceId || !itemCode) return item;

      const inventoryItem = itemQueryResult?.data?.data?.find((i: any) => i.itemCode === itemCode);
      const selectedPrice = inventoryItem?.prices?.find((p: any) => p.id === priceId);
      if (!selectedPrice) return item;

      const convertedUnitPrice = convertCurrencyAmount(
        Number(selectedPrice.effectivePrice) || 0,
        selectedPrice.currencyCode,
        transactionCurrencyCode,
        transactionCurrencyCode,
        transactionRate
      );

      return {
        ...item,
        unitPrice: convertedUnitPrice,
      };
    });

    form.setFieldsValue({ itemList: updatedItems });
    updatedItems.forEach((_, idx) => updateLineAmounts(idx));
  };

  const handleCurrencyChange = (currencyCode: string) => {
    const currencyData = currencyOptionsList?.find((c: any) => c.currencyCode === currencyCode);
    const isBase = currencyData?.isBaseCurrency || currencyCode === "USD";
    const rate = isBase ? 1 : getConfiguredCurrencyRate(currencyCode);
    const normalizedRate = rate > 0 ? rate : 1;

    setIsBaseCurrency(isBase);
    setSelectedCurrencyRate(normalizedRate);

    form.setFieldsValue({
      currencyTypeCode: currencyCode,
      exchangeRate: normalizedRate,
      exchangeRateDisplay: isBase ? 1 : toDisplayRate(normalizedRate),
      exchangeRateDate: new Date().toISOString()
    });

    recalculateItemPricesForTransactionCurrency(currencyCode, normalizedRate);
    updateTotalsWithServices();
  };

  const handleExchangeRateDisplayChange = (rate?: number | null) => {
    if (isBaseCurrency) return;
    const displayRate = Number(rate) || 0;
    const normalizedRate = toUsdPerUnitRate(displayRate);
    const safeRate = normalizedRate > 0 ? normalizedRate : 1;

    setSelectedCurrencyRate(safeRate);
    form.setFieldsValue({
      exchangeRate: safeRate,
      exchangeRateDisplay: displayRate > 0 ? displayRate : toDisplayRate(safeRate),
      exchangeRateDate: new Date().toISOString()
    });

    const transactionCurrencyCode = form.getFieldValue('currencyTypeCode') || 'USD';
    recalculateItemPricesForTransactionCurrency(transactionCurrencyCode, safeRate);
    updateTotalsWithServices();
  };

  const { isLoading: taxesLoading } = useQuery({
    queryKey: ['taxes'],
    queryFn: () => api.getTaxes(),
    onSuccess: (data) => {
      const options = data.map((tax: any) => ({
        label: `${tax.taxType} (${tax.rate}%)`,
        value: tax.id,
        data: tax
      }));
      setTaxOptions(options);
      if (options.length > 0) {
        setStandardTaxRate(options[0].data.rate);
      }
    },
    onError: (error) => {
      console.error("Error fetching taxes:", error);
    }
  });

  const handleItemSelect = async (value: string, name: number) => {
    const itemData = itemQueryResult?.data?.data?.find(
      (item: any) => item.itemCode === value
    );

    if (itemData) {
      const currentItems = form.getFieldValue('itemList') || [];
      const activePrices = itemData.prices?.filter((price: any) => price.isActive) || [];

      if (activePrices.length === 0) {
        message.error(`No active prices found for ${itemData.itemName}`);
        return;
      }

      const updatedItems = [...currentItems];
      updatedItems[name] = {
        ...updatedItems[name],
        InventoryItemId: itemData.id,
        itemCode: itemData.itemCode,
        itemName: itemData.itemName,
        itemClassCode: itemData.itemClassificationCode,
        unitPrice: 0,
        taxRate: itemData.tax?.rate || 0,
        taxType: itemData.tax?.taxType || "VAT",
        discountRate: 0,
        priceId: undefined,
        quantity: 1,
        isActive: true
      };

      form.setFieldsValue({ itemList: updatedItems });

      if (activePrices.length > 1) {
        message.info(`${itemData.itemName}: ${activePrices.length} pricing options available. Please select a price option.`);
      }
    }
  };

  const updateItemWithPrice = (itemData: any, price: any, name: number) => {
    const currentItems = form.getFieldValue('itemList') || [];
    let discountRate = 0;
    if (price.discountType === "Percentage") {
      discountRate = price.discountValue / 100;
    } else if (price.discountType === "FixedAmount" && price.basePrice > 0) {
      discountRate = price.discountValue / price.basePrice;
    }

    const taxRate = itemData.tax?.rate || 0;
    const taxType = itemData.tax?.taxType || "VAT";
    const transactionCurrencyCode = form.getFieldValue('currencyTypeCode') || 'USD';
    const transactionRate = isBaseCurrency ? 1 : (Number(form.getFieldValue('exchangeRate')) || selectedCurrencyRate || 1);
    const convertedUnitPrice = convertCurrencyAmount(
      Number(price.effectivePrice) || 0,
      price.currencyCode,
      transactionCurrencyCode,
      transactionCurrencyCode,
      transactionRate
    );

    const updatedItems = [...currentItems];
    updatedItems[name] = {
      ...updatedItems[name],
      InventoryItemId: itemData.id,
      itemCode: itemData.itemCode,
      itemName: itemData.itemName,
      itemClassCode: itemData.itemClassificationCode,
      unitPrice: convertedUnitPrice,
      taxRate: taxRate,
      taxType: taxType,
      discountRate: discountRate,
      priceId: price.id,
      quantity: updatedItems[name]?.quantity || 1,
      isActive: true
    };

    form.setFieldsValue({ itemList: updatedItems });

    updateLineAmounts(name);
  };

  const handlePriceSelect = (priceId: number, name: number) => {
    const itemData = itemQueryResult?.data?.data?.find(
      (item: any) => item.itemCode === form.getFieldValue(['itemList', name, 'itemCode'])
    );

    if (itemData) {
      const selectedPrice = itemData.prices?.find((price: any) => price.id === priceId);
      if (selectedPrice) {
        updateItemWithPrice(itemData, selectedPrice, name);
      }
    }
  };

  const generateInvoiceNo = (): string => {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let orderId = '';
    for (let i = 0; i < 8; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      orderId += characters[randomIndex];
    }
    return orderId;
  };

  useEffect(() => {
    const invoiceNo = generateInvoiceNo();
    form.setFieldsValue({
      invoiceNo,
      salesStatusCode: "PENDING",
      currencyTypeCode: "USD",
      exchangeRate: 1,
      exchangeRateDisplay: 1,
      exchangeRateDate: new Date().toISOString(),
      isActive: true,
      status: "PENDING",
      taxes: [],
      payments: [],
      transactionDate: dayjs()
    });
    setIsBaseCurrency(true);
    setSelectedCurrencyRate(1);
  }, [form]);

  const disableFutureDates = (current: dayjs.Dayjs) => {
    return current && current > dayjs().endOf('day');
  };

  const updateTotalsWithServices = () => {
    const currentItems = form.getFieldValue('itemList') || [];
    const billableServices = form.getFieldValue('billableServices') || [];

    const itemTotals = currentItems.reduce((acc: {
      totalItemCount: number;
      totalTaxableAmount: number;
      totalTax: number;
      totalAmount: number;
    }, item: SaleItem) => {
      const itemQuantity = Number(item.quantity) || 0;
      const itemTotal = Number(item.totalAmount) || 0;
      const itemTaxAmount = Number(item.taxAmount) || 0;
      const itemTaxableAmount = Number(item.taxableAmount) || (itemTotal - itemTaxAmount);

      return {
        totalItemCount: acc.totalItemCount + itemQuantity,
        totalTaxableAmount: acc.totalTaxableAmount + itemTaxableAmount,
        totalTax: acc.totalTax + itemTaxAmount,
        totalAmount: acc.totalAmount + itemTotal,
      };
    }, {
      totalItemCount: 0,
      totalTaxableAmount: 0,
      totalTax: 0,
      totalAmount: 0,
    });

    const serviceTotals = billableServices.reduce((acc: {
      totalTaxableAmount: number;
      totalTax: number;
      totalAmount: number;
    }, service: BillableService) => {
      const servicePrice = Number(service.price) || 0;
      const taxRate = (service.taxRate !== undefined ? service.taxRate : standardTaxRate) / 100;
      const taxDivisor = 1 + taxRate;
      const taxableAmount = taxDivisor !== 0 ? servicePrice / taxDivisor : 0;
      const taxAmount = servicePrice - taxableAmount;

      return {
        totalTaxableAmount: acc.totalTaxableAmount + taxableAmount,
        totalTax: acc.totalTax + taxAmount,
        totalAmount: acc.totalAmount + servicePrice,
      };
    }, {
      totalTaxableAmount: 0,
      totalTax: 0,
      totalAmount: 0,
    });

    const combinedTotals = {
      totalItemCount: itemTotals.totalItemCount + billableServices.length,
      totalTaxableAmount: itemTotals.totalTaxableAmount + serviceTotals.totalTaxableAmount,
      totalTax: itemTotals.totalTax + serviceTotals.totalTax,
      totalAmount: itemTotals.totalAmount + serviceTotals.totalAmount,
    };

    form.setFieldsValue({
      totalItemCount: combinedTotals.totalItemCount,
      totalTaxableAmount: combinedTotals.totalTaxableAmount,
      totalTax: combinedTotals.totalTax,
      totalAmount: combinedTotals.totalAmount,
    });

    syncDefaultTender(combinedTotals.totalAmount);
  };

  // Keep the single-tender "cash covers total" default alive without clobbering a
  // split payment the user has already configured.
  const syncDefaultTender = (total: number) => {
    const payments: Payment[] = form.getFieldValue('payments') || [];
    const currencyTypeCode = form.getFieldValue('currencyTypeCode') || 'USD';
    const exchangeRate = Number(form.getFieldValue('exchangeRate')) || 1;

    if (total <= 0 && payments.length === 0) return;

    if (payments.length === 0) {
      form.setFieldsValue({
        payments: [{
          PaymentMethod: 'CASH',
          Amount: total,
          currency: currencyTypeCode,
          exchangeRate
        }]
      });
      return;
    }

    if (payments.length === 1 && payments[0].currency === currencyTypeCode) {
      const updated = [{ ...payments[0], Amount: total, exchangeRate }];
      form.setFieldsValue({ payments: updated });
    }
  };

  const updateLineAmounts = (name: number) => {
    const currentItems = form.getFieldValue('itemList') || [];
    const currentItem = currentItems[name];

    if (!currentItem) return;

    const quantity = Number(currentItem.quantity) || 0;
    const unitPrice = Number(currentItem.unitPrice) || 0;
    const taxRate = (Number(currentItem.taxRate) || 0) / 100;
    const discountRate = Number(currentItem.discountRate) || 0;

    const lineAmount = quantity * unitPrice;
    const discountAmount = lineAmount * discountRate;
    const totalAmount = lineAmount - discountAmount;

    const taxDivisor = 1 + taxRate;
    const taxableAmount = taxDivisor !== 0 ? totalAmount / taxDivisor : 0;
    const taxAmount = totalAmount - taxableAmount;

    currentItems[name] = {
      ...currentItem,
      discountAmount,
      taxAmount,
      taxableAmount,
      totalAmount
    };

    form.setFieldsValue({ itemList: currentItems });
    updateTotalsWithServices();

    if (showBillableServices && currentItems.length > 0) {
      const currentBillableServices = form.getFieldValue('billableServices') || [];
      if (currentBillableServices.length > 0) {
        form.setFieldsValue({ billableServices: [...currentBillableServices] });
      }
    }
  };

  const handleQuantityChange = (name: number) => updateLineAmounts(name);
  const handlePriceChange = (name: number) => updateLineAmounts(name);
  const handleTaxRateChange = (name: number) => updateLineAmounts(name);
  const handleDiscountRateChange = (name: number) => updateLineAmounts(name);

  const handleFinish = async (values: SaleFormData) => {
    if (isManualCustomerEntry && (!values.customerTPIN || values.customerTPIN.trim() === '')) {
      values.customerTPIN = "0";
    }

    const taxMap: Record<string, Tax> = {};

    (values.itemList || []).forEach(item => {
      const taxType = item.taxType || "UNKNOWN";
      if (taxType === "UNKNOWN" || !item.taxAmount) return;

      const taxAmount = Number(item.taxAmount) || 0;
      const taxableAmount = Number(item.taxableAmount) || 0;
      const taxRatePercent = Number(item.taxRate) || 0;

      if (taxAmount <= 0) return;

      if (!taxMap[taxType]) {
        taxMap[taxType] = {
          TaxTypeCode: taxType,
          TaxableAmount: 0,
          TaxAmount: 0,
          TaxPercent: taxRatePercent
        };
      }

      taxMap[taxType].TaxableAmount += taxableAmount;
      taxMap[taxType].TaxAmount += taxAmount;
    });

    const billableServices = values.billableServices || [];
    if (billableServices.length > 0) {
      billableServices.forEach(service => {
        const servicePrice = Number(service.price) || 0;
        if (servicePrice <= 0) return;

        const servicesTaxType = service.taxType || "VAT";
        const taxRate = (service.taxRate !== undefined ? service.taxRate : standardTaxRate) / 100;

        const taxDivisor = 1 + taxRate;
        const taxableAmount = taxDivisor !== 0 ? servicePrice / taxDivisor : 0;
        const taxAmount = servicePrice - taxableAmount;

        if (taxAmount <= 0) return;

        if (!taxMap[servicesTaxType]) {
          taxMap[servicesTaxType] = {
            TaxTypeCode: servicesTaxType,
            TaxableAmount: 0,
            TaxAmount: 0,
            TaxPercent: service.taxRate || standardTaxRate
          };
        }

        taxMap[servicesTaxType].TaxableAmount += taxableAmount;
        taxMap[servicesTaxType].TaxAmount += taxAmount;
      });
    }

    const aggregatedTaxes = Object.values(taxMap);

    const currentItems = values.itemList || [];
    const billableServicesItems = values.billableServices || [];

    const itemTotals = {
      totalItemCount: currentItems.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0),
      totalTaxableAmount: currentItems.reduce((sum, item) => sum + (Number(item.taxableAmount) || 0), 0),
      totalTax: currentItems.reduce((sum, item) => sum + (Number(item.taxAmount) || 0), 0),
      totalAmount: currentItems.reduce((sum, item) => sum + (Number(item.totalAmount) || 0), 0),
    };

    const serviceTotals = billableServicesItems.reduce((acc: {
      totalTaxableAmount: number;
      totalTax: number;
      totalAmount: number;
    }, service: BillableService) => {
      const servicePrice = Number(service.price) || 0;
      const taxRate = standardTaxRate / 100;
      const taxDivisor = 1 + taxRate;
      const taxableAmount = taxDivisor !== 0 ? servicePrice / taxDivisor : 0;
      const taxAmount = servicePrice - taxableAmount;

      return {
        totalTaxableAmount: acc.totalTaxableAmount + taxableAmount,
        totalTax: acc.totalTax + taxAmount,
        totalAmount: acc.totalAmount + servicePrice,
      };
    }, {
      totalTaxableAmount: 0,
      totalTax: 0,
      totalAmount: 0,
    });

    const currencyTypeCode = values.currencyTypeCode || 'USD';
    const saleExchangeRate = Number(values.exchangeRate) || 1;

    if (!values.payments || values.payments.length === 0) {
      const totalAmount = (itemTotals.totalAmount + serviceTotals.totalAmount) || 0;
      values.payments = [{
        PaymentMethod: 'CASH',
        Amount: totalAmount,
        currency: currencyTypeCode,
        exchangeRate: saleExchangeRate
      }];
    }

    const totalDue = itemTotals.totalAmount + serviceTotals.totalAmount;
    const tenderedTotal = (values.payments || []).reduce((sum, payment) => {
      const amount = Number(payment.Amount) || 0;
      const tenderCurrency = payment.currency || currencyTypeCode;
      const tenderRate = Number(payment.exchangeRate) || getConfiguredCurrencyRate(tenderCurrency);
      return (
        sum +
        convertCurrencyAmount(
          amount,
          tenderCurrency,
          currencyTypeCode,
          currencyTypeCode,
          saleExchangeRate || tenderRate
        )
      );
    }, 0);

    const tenderTolerance = 0.01 * Math.max(1, values.payments.length);
    if (totalDue > 0 && tenderedTotal + tenderTolerance < totalDue) {
      form.setFields([
        {
          name: 'payments',
          errors: [
            `Tendered total (${tenderedTotal.toFixed(2)} ${currencyTypeCode}) is less than total (${totalDue.toFixed(2)} ${currencyTypeCode}).`
          ]
        }
      ]);
      return;
    }

    form.setFields([{ name: 'payments', errors: [] }]);

    // Normalise each tender so the backend always receives a currency + rate snapshot.
    const normalisedPayments = values.payments.map((p) => ({
      PaymentMethod: p.PaymentMethod || 'CASH',
      Amount: Number(p.Amount) || 0,
      Currency: p.currency || currencyTypeCode,
      ExchangeRate: Number(p.exchangeRate) || getConfiguredCurrencyRate(p.currency || currencyTypeCode),
      ExchangeRateDate: new Date().toISOString()
    }));

    const { currencyTypeCode: _currencyTypeCode, ...restValues } = values;
    const finalValues = {
      ...restValues,
      Currency: currencyTypeCode,
      taxes: aggregatedTaxes,
      totalItemCount: itemTotals.totalItemCount + billableServicesItems.length,
      totalTaxableAmount: itemTotals.totalTaxableAmount + serviceTotals.totalTaxableAmount,
      totalTax: itemTotals.totalTax + serviceTotals.totalTax,
      totalAmount: itemTotals.totalAmount + serviceTotals.totalAmount,
      payments: normalisedPayments,
      customerId: isManualCustomerEntry ? undefined : values.customerId
    };

    if (formProps.onFinish) {
      formProps.onFinish(finalValues);
    }
  };

  const handleCustomerSelect = (customerId: number) => {
    const selectedCustomer = customerQueryResult?.data?.data?.find(
      (customer: any) => customer.id === customerId
    );

    if (selectedCustomer) {
      form.setFieldsValue({
        customerId: selectedCustomer.id,
        customerTPIN: selectedCustomer.tpin || "0",
        customerName: selectedCustomer.name
      });
    }
  };

  const handleCashSaleToggle = (checked: boolean) => {
    setIsCashSale(checked);

    if (checked) {
      form.setFieldsValue({
        customerId: undefined,
        customerTPIN: "CASH-SALE",
        customerName: "Cash Customer"
      });

      const totalAmount = form.getFieldValue('totalAmount') || 0;
      const currencyTypeCode = form.getFieldValue('currencyTypeCode') || 'USD';
      const exchangeRate = Number(form.getFieldValue('exchangeRate')) || 1;

      form.setFieldsValue({
        payments: [{
          PaymentMethod: 'CASH',
          Amount: totalAmount,
          currency: currencyTypeCode,
          exchangeRate
        }]
      });
    } else {
      form.setFieldsValue({
        customerId: undefined,
        customerTPIN: undefined,
        customerName: undefined
      });
    }
  };

  const toggleBillableServices = (checked: boolean) => {
    setShowBillableServices(checked);
    setIsCustomerCollapsed(checked);

    if (!checked) {
      form.setFieldsValue({ billableServices: [] });
    } else {
      const currentBillableServices = form.getFieldValue('billableServices') || [];
      if (currentBillableServices.length === 0) {
        const defaultTaxId = taxOptions.length > 0 ? taxOptions[0].value : undefined;
        const defaultTaxType = taxOptions.length > 0 ? taxOptions[0].data.taxType : undefined;
        const defaultTaxRate = taxOptions.length > 0 ? taxOptions[0].data.rate : standardTaxRate;

        form.setFieldsValue({
          billableServices: [{
            description: '',
            price: 0,
            taxId: defaultTaxId,
            taxRate: defaultTaxRate,
            taxType: defaultTaxType
          }]
        });
      }
    }

    updateTotalsWithServices();
  };

  const toggleManualCustomerEntry = (checked: boolean) => {
    setIsManualCustomerEntry(checked);
    if (checked) {
      form.setFieldsValue({
        customerId: undefined,
        customerTPIN: '',
        customerName: '',
        customerContact: '',
        customerStreet: '',
        customerCity: '',
        customerProvince: '',
        customerCountry: 'Zimbabwe'
      });
    } else {
      form.setFieldsValue({
        customerId: undefined,
        customerName: '',
        customerContact: '',
        customerTPIN: undefined,
        customerStreet: '',
        customerCity: '',
        customerProvince: '',
        customerCountry: ''
      });
    }
  };

  const handleServiceTaxSelect = (taxId: number, index: number) => {
    const selectedTax = taxOptions.find(option => option.value === taxId);

    if (selectedTax) {
      const { taxType, rate } = selectedTax.data;

      const billableServices = form.getFieldValue('billableServices') || [];
      billableServices[index] = {
        ...billableServices[index],
        taxId: taxId,
        taxRate: rate,
        taxType: taxType
      };

      form.setFieldsValue({ billableServices });
      updateTotalsWithServices();
    }
  };

  return (
    <div className="page-container">
      <PageHeader
        title="Create New Sale"
        icon={<ShoppingCartOutlined />}
        breadcrumbs={[
          { title: "Sales", href: "/sales" },
          { title: "Create" },
        ]}
      />

      <Form
        {...formProps}
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{
          invoiceNo: generateInvoiceNo(),
          salesStatusCode: "PENDING",
          isActive: true,
          status: "PENDING"
        }}
        className="create-form-layout"
      >
        {/* Quick Options Card */}
        <div className="info-card" style={{ marginBottom: 16 }}>
          <div className="info-card-header">
            <span className="info-card-title">Quick Options</span>
          </div>
          <div className="info-card-body">
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} sm={8}>
                <Space align="center" size={8}>
                  <Switch
                    checked={isCashSale}
                    onChange={handleCashSaleToggle}
                    checkedChildren="Cash Sale"
                    unCheckedChildren="Regular"
                  />
                  <Text type="secondary" style={{ fontSize: '12px' }}>Quick cash sale</Text>
                </Space>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  name="transactionDate"
                  rules={[{ required: true, message: 'Date required' }]}
                  style={{ margin: 0 }}
                  label="Transaction Date"
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    disabledDate={disableFutureDates}
                    format="YYYY-MM-DD"
                    placeholder="Transaction date"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Space align="center" size={8}>
                  <Switch
                    checked={showBillableServices}
                    onChange={toggleBillableServices}
                    checkedChildren="Services"
                    unCheckedChildren="No Services"
                  />
                  <Text type="secondary" style={{ fontSize: '12px' }}>Add billable services</Text>
                </Space>
              </Col>
            </Row>
          </div>
        </div>

        {/* Transaction Currency */}
        <div className="info-card" style={{ marginBottom: 16 }}>
          <div className="info-card-header">
            <span className="info-card-title">Transaction</span>
          </div>
          <div className="info-card-body">
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="currencyTypeCode"
                  label="Currency"
                  rules={[{ required: true, message: 'Currency required' }]}
                  style={{ marginBottom: 0 }}
                >
                  <Select
                    {...currencySelectProps}
                    placeholder="Select currency"
                    onChange={handleCurrencyChange}
                    options={currencyOptionsList?.map((currency: any) => {
                      const isBase = !!currency.isBaseCurrency;
                      const usdPerUnit = isBase
                        ? 1
                        : normalizeToUsdPerUnit(Number(currency.exchangeRate) || 0, isBase);
                      const unitsPerUsd = isBase || usdPerUnit <= 0 ? 0 : unitsOfCurrencyPerUsd(usdPerUnit);
                      return {
                        label: isBase
                          ? `${currency.currencyCode} (Base)`
                          : `${currency.currencyCode} (1 USD = ${unitsPerUsd.toFixed(4)} ${currency.currencyCode})`,
                        value: currency.currencyCode,
                      };
                    }) || []}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                {!isBaseCurrency && (
                  <Form.Item
                    name="exchangeRateDisplay"
                    label="Exchange Rate (1 USD = ?)"
                    rules={[
                      { required: true, message: 'Exchange rate required' },
                      { type: 'number', min: 0.0001, message: 'Rate must be greater than 0' }
                    ]}
                    style={{ marginBottom: 0 }}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0.0001}
                      max={999999}
                      step={0.01}
                      precision={6}
                      placeholder="e.g. 32"
                      onChange={handleExchangeRateDisplayChange}
                    />
                  </Form.Item>
                )}
                {isBaseCurrency && (
                  <Form.Item name="exchangeRate" hidden>
                    <InputNumber value={1} max={999999} />
                  </Form.Item>
                )}
                {!isBaseCurrency && (
                  <Form.Item name="exchangeRate" hidden>
                    <InputNumber max={999999} />
                  </Form.Item>
                )}
                <Form.Item name="exchangeRateDate" hidden>
                  <Input />
                </Form.Item>
              </Col>
            </Row>
            {!isBaseCurrency && (
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
                Line prices and totals are in the selected currency. Enter how many units of that currency equal <Text strong>1 USD</Text> (e.g. ZiG 32); the sale stores the FX snapshot as USD per 1 unit for reporting and fiscalisation.
              </Text>
            )}
          </div>
        </div>

        {/* Customer Information */}
        <div className="info-card" style={{ marginBottom: 16 }}>
          <div className="info-card-header">
            <span className="info-card-title">Customer Information</span>
            <Badge
              count={form.getFieldValue('salesStatusCode') === "COMPLETED" ? "COMPLETED" : "PENDING"}
              style={{
                backgroundColor: form.getFieldValue('salesStatusCode') === "COMPLETED" ? "#10b981" : "#94a3b8",
                color: "#fff",
                fontSize: '11px'
              }}
            />
          </div>
          <div className="info-card-body">
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={6}>
                <Form.Item
                  label="Invoice No"
                  name="invoiceNo"
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <Input readOnly style={{ fontWeight: 'bold' }} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={6}>
                <Form.Item label=" ">
                  <Space align="center" size={8}>
                    <Switch
                      checked={isManualCustomerEntry}
                      onChange={toggleManualCustomerEntry}
                      checkedChildren="New Customer"
                      unCheckedChildren="Select Existing"
                      disabled={isCashSale}
                    />
                    <Tooltip title="Toggle to create a new customer or select existing">
                      <InfoCircleOutlined style={{ color: '#3b82f6', fontSize: '14px' }} />
                    </Tooltip>
                  </Space>
                </Form.Item>
              </Col>

              {!isManualCustomerEntry ? (
                <Col xs={24} sm={24} md={12}>
                  <Form.Item
                    label="Select Customer"
                    name="customerId"
                    help={<Text type="secondary" style={{ fontSize: '11px' }}>Select from existing customers</Text>}
                  >
                    <Select
                      placeholder={isCashSale ? "Cash Customer" : "Search customer"}
                      showSearch
                      disabled={isCashSale}
                      onChange={handleCustomerSelect}
                      filterOption={(input: string, option: any) =>
                        option?.label?.toString().toLowerCase().includes(input.toLowerCase())
                      }
                      options={
                        customerQueryResult?.data?.data?.map((customer: any) => {
                          const isManualEntry = !customer.tpin || customer.tpin === "0";
                          const label = isManualEntry
                            ? `${customer.name} (Manual Entry)`
                            : `${customer.name} (${customer.tpin})`;
                          return { label, value: customer.id, data: customer };
                        }) || []
                      }
                    />
                  </Form.Item>
                  <Form.Item name="customerTPIN" hidden><Input /></Form.Item>
                  <Form.Item name="customerName" hidden><Input /></Form.Item>
                </Col>
              ) : (
                <>
                  <Col span={24}>
                    <Alert
                      message="Create New Customer"
                      description="Enter details for a new customer. This customer will be saved for future transactions."
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                      banner
                    />
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Form.Item
                      label="Customer Name"
                      name="customerName"
                      rules={[
                        { required: isManualCustomerEntry, message: 'Customer name required' },
                        { min: 2, message: 'Min 2 characters' }
                      ]}
                    >
                      <Input placeholder="Customer full name" disabled={isCashSale} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Form.Item label="TPIN (Optional)" name="customerTPIN">
                      <Input placeholder="TPIN (optional)" disabled={isCashSale} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Form.Item
                      label="Contact"
                      name="customerContact"
                      rules={[{ required: isManualCustomerEntry, message: 'Contact required' }]}
                    >
                      <Input placeholder="Phone/Email" disabled={isCashSale} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Form.Item
                      label="Street Address"
                      name="customerStreet"
                      rules={[{ required: isManualCustomerEntry, message: 'Address required' }]}
                    >
                      <Input placeholder="Street address" disabled={isCashSale} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8} md={4}>
                    <Form.Item
                      label="City"
                      name="customerCity"
                      rules={[{ required: isManualCustomerEntry, message: 'City required' }]}
                    >
                      <Input placeholder="City" disabled={isCashSale} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8} md={4}>
                    <Form.Item
                      label="Province"
                      name="customerProvince"
                      rules={[{ required: isManualCustomerEntry, message: 'Province required' }]}
                    >
                      <Select
                        placeholder="Province"
                        disabled={isCashSale}
                        options={[
                          { label: 'Harare', value: 'Harare' },
                          { label: 'Bulawayo', value: 'Bulawayo' },
                          { label: 'Manicaland', value: 'Manicaland' },
                          { label: 'Mashonaland Central', value: 'Mashonaland Central' },
                          { label: 'Mashonaland East', value: 'Mashonaland East' },
                          { label: 'Mashonaland West', value: 'Mashonaland West' },
                          { label: 'Masvingo', value: 'Masvingo' },
                          { label: 'Matabeleland North', value: 'Matabeleland North' },
                          { label: 'Matabeleland South', value: 'Matabeleland South' },
                          { label: 'Midlands', value: 'Midlands' }
                        ]}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8} md={4}>
                    <Form.Item
                      label="Country"
                      name="customerCountry"
                      initialValue="Zimbabwe"
                      rules={[{ required: isManualCustomerEntry, message: 'Country required' }]}
                    >
                      <Select
                        placeholder="Country"
                        disabled={isCashSale}
                        options={[
                          { label: 'Zimbabwe', value: 'Zimbabwe' },
                          { label: 'South Africa', value: 'South Africa' },
                          { label: 'Botswana', value: 'Botswana' },
                          { label: 'Zambia', value: 'Zambia' },
                          { label: 'Mozambique', value: 'Mozambique' },
                          { label: 'Other', value: 'Other' }
                        ]}
                      />
                    </Form.Item>
                  </Col>
                </>
              )}

              {!isManualCustomerEntry && (
                <Form.Item name="customerName" hidden><Input /></Form.Item>
              )}

              <Col span={24}>
                <Form.Item label="Remarks" name="remark">
                  <Input.TextArea rows={2} placeholder="Additional notes..." />
                </Form.Item>
              </Col>
            </Row>
          </div>
        </div>

        {/* Billable Services */}
        {showBillableServices && (
          <div className="data-table-card" style={{ marginBottom: 16 }}>
            <Card
              title={
                <Space>
                  <span>Billable Services</span>
                  <Badge count={form.getFieldValue('billableServices')?.length || 0} style={{ backgroundColor: '#3b82f6' }} />
                </Space>
              }
            >
              <Form.List name="billableServices">
                {(fields: FormListFieldData[], { add, remove }: FormListOperation) => (
                  <>
                    {fields.map(({ key, name, ...restField }: any, index: number) => (
                      <Card
                        key={key}
                        size="small"
                        type="inner"
                        style={{ marginBottom: 12 }}
                        title={`Service #${index + 1}`}
                        extra={
                          <Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => remove(name)} />
                        }
                      >
                        <Row gutter={[16, 16]}>
                          <Col xs={24} sm={24} md={12}>
                            <Form.Item
                              {...restField}
                              label="Description"
                              name={[name, 'description']}
                              rules={[{ required: true, message: 'Required' }]}
                            >
                              <Input.TextArea rows={2} placeholder="Service description..." />
                            </Form.Item>
                          </Col>
                          <Col xs={12} sm={12} md={6}>
                            <Form.Item
                              {...restField}
                              label="Tax"
                              name={[name, 'taxId']}
                              rules={[{ required: true, message: 'Required' }]}
                            >
                              <Select
                                placeholder="Tax type"
                                loading={taxesLoading}
                                options={taxOptions}
                                onChange={(value) => handleServiceTaxSelect(value, name)}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={12} sm={12} md={6}>
                            <Form.Item
                              {...restField}
                              label="Price"
                              name={[name, 'price']}
                              rules={[
                                { required: true, message: 'Required' },
                                { type: 'number', min: 0.01, message: 'Must be > 0' }
                              ]}
                            >
                              <InputNumber
                                placeholder="0.00"
                                min={0}
                                max={999999}
                                precision={2}
                                style={{ width: '100%' }}
                                onChange={() => updateTotalsWithServices()}
                              />
                            </Form.Item>
                          </Col>
                          <Form.Item {...restField} name={[name, 'taxRate']} hidden><InputNumber /></Form.Item>
                          <Form.Item {...restField} name={[name, 'taxType']} hidden><Input /></Form.Item>
                        </Row>
                      </Card>
                    ))}

                    <Button
                      type="dashed"
                      onClick={() => {
                        const defaultTaxId = taxOptions.length > 0 ? taxOptions[0].value : undefined;
                        const defaultTaxType = taxOptions.length > 0 ? taxOptions[0].data.taxType : undefined;
                        const defaultTaxRate = taxOptions.length > 0 ? taxOptions[0].data.rate : standardTaxRate;
                        add({
                          description: '',
                          price: 0,
                          taxId: defaultTaxId,
                          taxRate: defaultTaxRate,
                          taxType: defaultTaxType
                        });
                      }}
                      block
                      icon={<PlusOutlined />}
                      style={{ marginTop: 8 }}
                    >
                      Add Service
                    </Button>
                  </>
                )}
              </Form.List>
            </Card>
          </div>
        )}

        {/* Sale Items */}
        <div className="data-table-card" style={{ marginBottom: 16 }}>
          <Card
            title={
              <Space>
                <span>Sale Items</span>
                <Badge count={form.getFieldValue('itemList')?.length || 0} style={{ backgroundColor: '#3b82f6' }} />
              </Space>
            }
          >
            <Form.List name="itemList">
              {(fields: FormListFieldData[], { add, remove }: FormListOperation) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Card
                      key={key}
                      size="small"
                      type="inner"
                      style={{ marginBottom: 12 }}
                      extra={
                        <Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => remove(name)} />
                      }
                    >
                      <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} md={8}>
                          <Form.Item
                            {...restField}
                            label="Item Code"
                            name={[name, 'itemCode']}
                            rules={[{ required: true, message: 'Required' }]}
                          >
                            <Select
                              showSearch
                              placeholder="Search by code or name"
                              onChange={(value) => handleItemSelect(value, name)}
                              filterOption={(input: string, option: any) => {
                                if (!input || !option?.label) return true;
                                return option.label.toString().toLowerCase().includes(input.toLowerCase());
                              }}
                              options={
                                itemQueryResult?.data?.data?.map((item: any) => ({
                                  label: `${item.itemCode} - ${item.itemName}`,
                                  value: item.itemCode
                                })) || []
                              }
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                          <Form.Item
                            {...restField}
                            label="Item Name"
                            name={[name, 'itemName']}
                            rules={[{ required: true, message: 'Required' }]}
                          >
                            <Input readOnly placeholder="Item Name" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                          <Form.Item
                            {...restField}
                            label="Price Option"
                            name={[name, 'priceId']}
                            rules={[{ required: true, message: 'Select price' }]}
                          >
                            <Select
                              placeholder="Select pricing option"
                              onChange={(priceId) => handlePriceSelect(priceId, name)}
                              options={(() => {
                                const itemCode = form.getFieldValue(['itemList', name, 'itemCode']);
                                if (!itemCode) return [];
                                const item = itemQueryResult?.data?.data?.find((item: any) => item.itemCode === itemCode);
                                if (!item) return [];
                                const activePrices = item.prices?.filter((price: any) => price.isActive) || [];
                                return activePrices.map((price: any) => {
                                  let discountText = '';
                                  if (price.discountType === 'Percentage' && price.discountValue > 0) {
                                    discountText = ` (-${price.discountValue}%)`;
                                  } else if (price.discountType === 'FixedAmount' && price.discountValue > 0) {
                                    discountText = ` (-${price.currencyCode} ${price.discountValue})`;
                                  }
                                  const priceName = price.name || 'Default';
                                  const priceDisplay = `${price.currencyCode} ${price.effectivePrice.toFixed(2)}${discountText}`;
                                  return { label: `${priceName}: ${priceDisplay}`, value: price.id };
                                });
                              })()}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={12} sm={8} md={6}>
                          <Form.Item
                            {...restField}
                            label="Quantity"
                            name={[name, 'quantity']}
                            rules={[{ required: true, message: 'Required' }]}
                          >
                            <InputNumber
                              placeholder="Qty"
                              min={0}
                              max={999999}
                              onChange={() => handleQuantityChange(name)}
                              style={{ width: '100%' }}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={12} sm={8} md={6}>
                          <Form.Item {...restField} label="Unit Price" name={[name, 'unitPrice']}>
                            <InputNumber
                              placeholder="Price"
                              min={0}
                              max={999999}
                              precision={3}
                              readOnly
                              style={{ width: '100%' }}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={12} sm={8} md={6}>
                          <Form.Item
                            {...restField}
                            label="Tax Rate (%)"
                            name={[name, 'taxRate']}
                            rules={[{ required: true, message: 'Required' }]}
                          >
                            <InputNumber
                              placeholder="Tax Rate"
                              min={0}
                              max={100}
                              step={0.01}
                              formatter={(value) => `${value}%`}
                              parser={(value: string) => value ? Number(value.replace('%', '')) : 0}
                              onChange={() => handleTaxRateChange(name)}
                              style={{ width: '100%' }}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={8} md={6}>
                          <Form.Item {...restField} label="Total Amount" name={[name, 'totalAmount']}>
                            <InputNumber readOnly placeholder="Total" min={0} max={999999} precision={3} style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>

                        {/* Hidden fields */}
                        <Form.Item {...restField} name={[name, 'itemClassCode']} hidden><Input /></Form.Item>
                        <Form.Item {...restField} name={[name, 'discountRate']} hidden><InputNumber min={0} max={1} step={0.01} /></Form.Item>
                        <Form.Item {...restField} name={[name, 'discountAmount']} hidden><InputNumber min={0} max={999999} precision={3} /></Form.Item>
                        <Form.Item {...restField} name={[name, 'taxAmount']} hidden><InputNumber min={0} max={999999} precision={3} /></Form.Item>
                        <Form.Item {...restField} name={[name, 'taxableAmount']} hidden><InputNumber min={0} max={999999} precision={3} /></Form.Item>
                        <Form.Item {...restField} name={[name, 'priceId']} hidden><Input /></Form.Item>
                      </Row>
                    </Card>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                    style={{ marginTop: 8 }}
                  >
                    Add Item
                  </Button>
                </>
              )}
            </Form.List>
          </Card>
        </div>

        {/* Summary and Payment */}
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <div className="info-card">
              <div className="info-card-header">
                <span className="info-card-title">Sale Summary</span>
              </div>
              <div className="info-card-body">
                <Form.Item shouldUpdate={(prevValues: any, currentValues: any) =>
                  prevValues.totalTaxableAmount !== currentValues.totalTaxableAmount ||
                  prevValues.totalTax !== currentValues.totalTax ||
                  prevValues.totalAmount !== currentValues.totalAmount ||
                  prevValues.currencyTypeCode !== currentValues.currencyTypeCode
                }>
                  {({ getFieldValue }: any) => {
                    const currency = getFieldValue('currencyTypeCode') || 'USD';
                    return (
                      <Space direction="vertical" size={8} style={{ width: '100%' }}>
                        <Row justify="space-between">
                          <Text type="secondary">Subtotal:</Text>
                          <Text>{currency} {(getFieldValue('totalTaxableAmount') || 0).toFixed(2)}</Text>
                        </Row>
                        <Row justify="space-between">
                          <Text type="secondary">Tax:</Text>
                          <Text>{currency} {(getFieldValue('totalTax') || 0).toFixed(2)}</Text>
                        </Row>
                        <Row justify="space-between" style={{ borderTop: '1px solid var(--surface-border)', paddingTop: 8 }}>
                          <Text strong>Total:</Text>
                          <Text strong style={{ fontSize: 18, color: 'var(--color-primary)' }}>
                            {currency} {(getFieldValue('totalAmount') || 0).toFixed(2)}
                          </Text>
                        </Row>
                      </Space>
                    );
                  }}
                </Form.Item>

                {/* Hidden form fields for summary */}
                <Form.Item name="totalItemCount" hidden><InputNumber min={0} max={999999} /></Form.Item>
                <Form.Item name="totalTaxableAmount" hidden><InputNumber min={0} max={999999} precision={3} /></Form.Item>
                <Form.Item name="totalTax" hidden><InputNumber min={0} max={999999} precision={3} /></Form.Item>
                <Form.Item name="totalAmount" hidden><InputNumber min={0} max={999999} precision={3} /></Form.Item>
              </div>
            </div>
          </Col>

          <Col xs={24} md={12}>
            <div className="info-card">
              <div className="info-card-header">
                <span className="info-card-title">Payment Tenders</span>
              </div>
              <div className="info-card-body">
                <Form.List name="payments">
                  {(fields, { add, remove }) => (
                    <TenderPanel
                      fields={fields}
                      onAdd={add}
                      onRemove={remove}
                      form={form}
                      currencyOptions={currencyOptionsList}
                      getConfiguredCurrencyRate={getConfiguredCurrencyRate}
                      convertCurrencyAmount={convertCurrencyAmount}
                    />
                  )}
                </Form.List>
                <Form.Item shouldUpdate noStyle>
                  {() => {
                    const errors = form.getFieldError('payments');
                    if (errors.length === 0) return null;
                    return (
                      <Text type="danger" style={{ display: 'block', marginTop: 8 }}>
                        {errors[0]}
                      </Text>
                    );
                  }}
                </Form.Item>
              </div>
            </div>
          </Col>
        </Row>

        {/* Hidden form fields */}
        <Form.Item name="isActive" hidden><Input /></Form.Item>
        <Form.Item name="status" hidden><Input /></Form.Item>

        {/* Submit Button */}
        <Button {...saveButtonProps} type="primary" size="large" style={{ width: '100%', marginTop: 24 }}>
          Create Sale
        </Button>
      </Form>
    </div>
  );
}
